import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { BellOff, ChevronLeft, Copy, Plus, Trash2 } from 'lucide-react';
import { mensajeDeError } from '../lib/mensajeDeError';

/**
 * MI HORARIO (artefacto «Mi semana»: «La primera vez», fichas 20, 21, 24 y 25).
 *
 *     «Todavía no has puesto tus horarios. Estas son tus comidas. Ponles la hora a la que
 *      las haces y te queda el día montado.»
 *
 * LO RELLENA ÉL, como dijo Francisco. Lo único que se le da hecho es LA LISTA DE SUS
 * COMIDAS, que ya está en su plan: «no tiene sentido hacerle escribir "Comida 1"». Él pone
 * las horas.
 *
 * Y DOS DÍAS, NO SIETE: uno de entreno y uno de descanso. «Cuando lo tengas, lo copias a
 * los demás días y ya está la semana. No se rellena siete veces.»
 */

// El nombre con el que se le habla de cada hueco. El peri va con su nombre corto, que es
// como se llama en toda la app.
const NOMBRE = {
    C1: 'Comida 1', C2: 'Comida 2', C3: 'Comida 3',
    C4: 'Comida 4', C5: 'Comida 5', C6: 'Comida 6',
    Intra: 'Intra', Post: 'Post',
};

// EL TOPE DE COSAS CON HORA EN UN DÍA: «si se pone ocho en un día, que la app se lo diga».
// Ocho es el número que da él. Lo mismo que `TOPE_DE_COSAS_CON_HORA` en `core/horarios.py`:
// aquí se cuenta en pantalla porque se le dice MIENTRAS las pone, no después de guardar.
const TOPE = 8;

/** Cuántas cosas de ese día pueden sonar. Levantarse no cuenta: no avisa de nada, y
 *  sumarlo le haría pasarse del tope por una hora que no suena. */
const cuantasConHora = (dia) => {
    const d = dia || {};
    let n = Object.values(d.comidas || {}).filter(Boolean).length;
    if ((d.entreno || {}).hora) n += 1;
    if ((d.cardio || {}).hora) n += 1;
    n += (d.extras || []).filter(e => e && e.hora).length;
    if (d.dormir) n += 1;
    return n;
};

/** «Martes, 15 de septiembre», que es como se lee una fecha, no «2026-09-15». */
const enPalabras = (fecha) => {
    const d = new Date(`${fecha}T12:00:00`);
    if (Number.isNaN(d.getTime())) return fecha;
    const texto = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    return texto.charAt(0).toUpperCase() + texto.slice(1);
};

/** Una fila con su hora. El `input type="time"` es el del teléfono, que es el que la gente
 *  sabe usar; escribir «7:30» a mano es la forma más rápida de que te pongan las 7 y 30 de
 *  la tarde. */
const FilaDeHora = ({ titulo, ayuda, valor, onCambiar, testId, onQuitar }) => (
    <div className="surface p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{titulo}</p>
            {ayuda && <p className="text-xs text-muted-foreground mt-0.5">{ayuda}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
            <input type="time" value={valor || ''} data-testid={testId}
                onChange={(e) => onCambiar(e.target.value)}
                aria-label={`Hora de ${titulo}`}
                className={`bg-muted border rounded-lg px-2.5 py-1.5 text-sm font-data
                            focus:outline-none focus:border-brand ${
                    valor ? 'border-border text-foreground' : 'border-brand/40 text-muted-foreground'}`} />
            {onQuitar && (
                <button type="button" onClick={onQuitar} aria-label={`Quitar ${titulo}`}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted">
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    </div>
);

const MiHorarioPage = () => {
    const navigate = useNavigate();
    const { api } = useAuth();
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [tipo, setTipo] = useState('entreno');
    const [horarios, setHorarios] = useState({});
    const [comidas, setComidas] = useState({ entreno: [], descanso: [] });
    const [puesto, setPuesto] = useState(true);
    // Lo que le ha pautado su entrenador, dicho en una línea: «Martes y jueves, 30 min
    // andando». Sin pauta no se le promete ningún cardio.
    const [cardioPautado, setCardioPautado] = useState(null);
    // El día que está eligiendo para dejarlo callado. Vacío hasta que toca el calendario.
    const [diaSuelto, setDiaSuelto] = useState('');

    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const { data } = await api.get('/diets/horario');
                if (!vivo) return;
                setHorarios(data.horarios || {});
                setComidas(data.comidas || { entreno: [], descanso: [] });
                setPuesto(Boolean(data.puesto));
                setCardioPautado(data.cardio_en_palabras || null);
            } catch {
                if (vivo) toast.error('No pudimos traer tus horarios');
            } finally {
                if (vivo) setCargando(false);
            }
        })();
        return () => { vivo = false; };
    }, [api]);

    const dia = horarios[tipo] || {};
    const clavesDeComida = comidas[tipo] || [];

    // Cambiar una hora no guarda: se guarda al pulsar, para que no se dispare una petición
    // por cada dígito que toca en el reloj del móvil.
    const cambiar = (camino, valor) => {
        setHorarios(prev => {
            const copia = { ...prev, [tipo]: { ...(prev[tipo] || {}) } };
            const suyo = copia[tipo];
            if (camino === 'levantarse' || camino === 'dormir') {
                if (valor) suyo[camino] = valor; else delete suyo[camino];
            } else if (camino.startsWith('comida:')) {
                const clave = camino.slice(7);
                suyo.comidas = { ...(suyo.comidas || {}) };
                if (valor) suyo.comidas[clave] = valor; else delete suyo.comidas[clave];
            } else if (camino === 'entreno' || camino === 'entreno_hasta') {
                suyo.entreno = { ...(suyo.entreno || {}) };
                const campo = camino === 'entreno' ? 'hora' : 'hasta';
                if (valor) suyo.entreno[campo] = valor; else delete suyo.entreno[campo];
                if (!suyo.entreno.hora) delete suyo.entreno;
            } else if (camino === 'cardio') {
                suyo.cardio = { ...(suyo.cardio || {}) };
                if (valor) suyo.cardio.hora = valor; else delete suyo.cardio;
            }
            return copia;
        });
    };

    const cambiarExtra = (i, campo, valor) => {
        setHorarios(prev => {
            const copia = { ...prev, [tipo]: { ...(prev[tipo] || {}) } };
            const extras = [...(copia[tipo].extras || [])];
            extras[i] = { ...extras[i], [campo]: valor };
            copia[tipo].extras = extras;
            return copia;
        });
    };

    const quitarExtra = (i) => {
        setHorarios(prev => {
            const copia = { ...prev, [tipo]: { ...(prev[tipo] || {}) } };
            copia[tipo].extras = (copia[tipo].extras || []).filter((_, j) => j !== i);
            return copia;
        });
    };

    const anadirExtra = () => {
        setHorarios(prev => {
            const copia = { ...prev, [tipo]: { ...(prev[tipo] || {}) } };
            copia[tipo].extras = [...(copia[tipo].extras || []), { que: 'Andar', hora: '', minutos: 20 }];
            return copia;
        });
    };

    const guardar = async () => {
        if (guardando) return;
        setGuardando(true);
        try {
            const { data } = await api.put('/diets/horario', { horarios });
            setHorarios(data.horarios || {});
            setPuesto(Boolean(data.puesto));
            toast.success('Horario guardado');
        } catch {
            toast.error('No pudimos guardar tu horario');
        } finally {
            setGuardando(false);
        }
    };

    // «Cuando lo tengas, lo copias a los demás días»: primero se guarda lo que tiene en
    // pantalla, que si no se copiaría lo de antes y el cliente vería copiado algo distinto
    // de lo que está mirando.
    const copiar = async () => {
        if (guardando) return;
        setGuardando(true);
        try {
            await api.put('/diets/horario', { horarios });
            const destino = tipo === 'entreno' ? 'descanso' : 'entreno';
            const { data } = await api.post('/diets/horario/copiar', { origen: tipo, destino });
            setHorarios(data.horarios || {});
            setPuesto(Boolean(data.puesto));
            toast.success(destino === 'descanso'
                ? 'Copiado a tus días de descanso'
                : 'Copiado a tus días de entreno');
        } catch (e) {
            toast.error(mensajeDeError(e, 'No pudimos copiarlo'));
        } finally {
            setGuardando(false);
        }
    };

    const tieneAlgo = useMemo(() => Boolean(
        dia.levantarse || dia.dormir || Object.keys(dia.comidas || {}).length || dia.entreno
    ), [dia]);

    // EL TOPE, CONTADO MIENTRAS LAS PONE (ficha 07): «un tope: si se pone ocho en un día,
    // que la app se lo diga». Se le dice y se guarda igual: es un aviso, no un candado.
    const conHora = useMemo(() => cuantasConHora(dia), [dia]);

    // LOS DÍAS EN SILENCIO (ficha 07): «los días marcados como excepción van en silencio».
    // Se guardan en `horarios.dias`, que es donde viven las excepciones por fecha: un día
    // en silencio es una excepción más, solo que lo que cambia es que no suene.
    const diasCallados = useMemo(() => Object.entries(horarios.dias || {})
        .filter(([, d]) => d && d.silencio === true)
        .map(([fecha]) => fecha)
        .sort(), [horarios]);

    const marcarEnSilencio = (fecha) => {
        if (!fecha) return;
        setHorarios(prev => ({
            ...prev,
            dias: { ...(prev.dias || {}), [fecha]: { ...((prev.dias || {})[fecha] || {}), silencio: true } },
        }));
    };

    // Quitar el silencio NO borra el día: si tenía horas propias se quedan como estaban, y
    // lo único que se va es la marca de callado.
    const quitarSilencio = (fecha) => {
        setHorarios(prev => {
            const dias = { ...(prev.dias || {}) };
            const suyo = { ...(dias[fecha] || {}) };
            delete suyo.silencio;
            if (Object.keys(suyo).length) dias[fecha] = suyo; else delete dias[fecha];
            return { ...prev, dias };
        });
    };

    if (cargando) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand border-t-transparent" />
            </div>
        );
    }

    return (
        <div className="max-w-xl mx-auto px-4 lg:px-0 pb-28" data-testid="mi-horario">
            <button type="button" onClick={() => navigate('/dashboard/semana')}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
                <ChevronLeft className="w-4 h-4" /> Mi semana
            </button>

            <h1 className="titulo-pantalla">MI HORARIO</h1>

            {/* SU CAJA DE LA PRIMERA VEZ, con sus palabras. Solo mientras no haya puesto
                nada: una vez puesto, repetirle que no lo ha puesto es faltar a la verdad. */}
            {!puesto && (
                <div className="surface p-4 mt-4 border border-brand/40 bg-brand/[0.06]"
                    data-testid="horario-primera-vez">
                    <p className="text-sm font-bold text-foreground">Todavía no has puesto tus horarios</p>
                </div>
            )}

            {/* DOS DÍAS Y NO SIETE. El de descanso no lleva entreno ni peri, así que sus
                filas son otras: eso lo decide el servidor con la lista de comidas. */}
            <div className="grid grid-cols-2 bg-muted rounded-xl p-1 gap-1 mt-4" role="tablist">
                {[['entreno', 'Día de entreno'], ['descanso', 'Día de descanso']].map(([id, texto]) => (
                    <button key={id} role="tab" aria-selected={tipo === id}
                        onClick={() => setTipo(id)} data-testid={`horario-${id}`}
                        className={`px-3 py-2 rounded-lg text-sm font-bold transition-colors ${
                            tipo === id ? 'bg-brand text-[#141414]' : 'text-muted-foreground hover:text-foreground'}`}>
                        {texto}
                    </button>
                ))}
            </div>

            <div className="space-y-2 mt-4">
                <FilaDeHora titulo="Te levantas" valor={dia.levantarse} testId="hora-levantarse"
                    onCambiar={(v) => cambiar('levantarse', v)} />

                {clavesDeComida.map(clave => (
                    <FilaDeHora key={clave} titulo={NOMBRE[clave] || clave}
                        valor={(dia.comidas || {})[clave]} testId={`hora-${clave}`}
                        onCambiar={(v) => cambiar(`comida:${clave}`, v)} />
                ))}

                {tipo === 'entreno' && (
                    <>
                        <FilaDeHora titulo="Entreno"
                            valor={(dia.entreno || {}).hora} testId="hora-entreno"
                            onCambiar={(v) => cambiar('entreno', v)} />
                        {(dia.entreno || {}).hora && (
                            <FilaDeHora titulo="Acabas de entrenar"
                                valor={(dia.entreno || {}).hasta} testId="hora-entreno-hasta"
                                onCambiar={(v) => cambiar('entreno_hasta', v)} />
                        )}
                        {/* EL CARDIO PAUTADO (ficha 24): «el que le pones tú, con su bloque
                            propio en el horario: su hora, su duración y su aviso».

                            LOS DÍAS Y LOS MINUTOS SON DE SU ENTRENADOR, la hora es suya. Si
                            no le han pautado nada se dice, en vez de dejarle adivinar si le
                            toca: hasta hoy la app le preguntaba cada noche si había hecho
                            cardio sin haberle dicho nunca que tuviera. */}
                        <FilaDeHora titulo="Cardio pautado"
                            ayuda={cardioPautado || 'Todavía no te han pautado cardio'}
                            valor={(dia.cardio || {}).hora} testId="hora-cardio"
                            onCambiar={(v) => cambiar('cardio', v)} />
                    </>
                )}

                <FilaDeHora titulo="A dormir" valor={dia.dormir} testId="hora-dormir"
                    onCambiar={(v) => cambiar('dormir', v)} />
            </div>

            {/* EL CARDIO EXTRA, QUE SON HUECOS RESERVADOS (ficha 25): «no es una hora: es
                todos los días -- andar, escaleras, salir a dar una vuelta --». Tienen nombre
                porque el nombre es lo que hace que se cumplan. */}
            <div className="mt-5">
                <p className="caption mb-2">Cardio extra</p>
                <div className="space-y-2">
                    {(dia.extras || []).map((e, i) => (
                        <div key={i} className="surface p-3 flex items-center gap-2">
                            <input type="text" value={e.que || ''} placeholder="Andar, escaleras..."
                                onChange={(ev) => cambiarExtra(i, 'que', ev.target.value)}
                                aria-label="Qué vas a hacer" data-testid={`extra-que-${i}`}
                                className="flex-1 min-w-0 bg-muted border border-border rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-brand" />
                            <input type="time" value={e.hora || ''} data-testid={`extra-hora-${i}`}
                                onChange={(ev) => cambiarExtra(i, 'hora', ev.target.value)}
                                aria-label="A qué hora"
                                className="bg-muted border border-border rounded-lg px-2.5 py-1.5 text-sm font-data focus:outline-none focus:border-brand" />
                            <button type="button" onClick={() => quitarExtra(i)} aria-label="Quitar este hueco"
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted flex-shrink-0">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={anadirExtra} data-testid="anadir-cardio-extra"
                        className="w-full surface surface-hover p-3 flex items-center justify-center gap-2 text-sm font-bold text-brand">
                        <Plus className="w-4 h-4" /> Añadir cardio extra
                    </button>
                </div>
            </div>

            {/* EL TOPE (ficha 07): «un tope: si se pone ocho en un día, que la app se lo
                diga». SE LO DICE Y LE DEJA GUARDAR: el que quiere once horas las pone, pero
                sabiendo cuántas veces le va a sonar. Naranja, que es el color de la casa
                para lo que se pasa. */}
            {conHora > TOPE && (
                <div className="surface p-4 mt-4 border border-brand/40 bg-brand/[0.06]"
                    data-testid="horario-tope" role="status">
                    <p className="text-sm font-bold text-foreground">
                        Llevas {conHora} cosas con hora en este día
                    </p>
                </div>
            )}

            {/* LOS DÍAS EN SILENCIO (ficha 07): «los días marcados como excepción van en
                silencio». El día que se sale de lo normal -- una boda, un viaje, un día de
                fiesta -- no necesita que le recuerden sus seis comidas. */}
            <div className="mt-5">
                <p className="caption mb-2">Días en silencio</p>
                <div className="surface p-4">
                    {/* APILADOS EN EL MÓVIL: a 390 el campo de fecha y el botón no caben en
                        la misma fila, y el calendario del teléfono salía cortado a la
                        mitad. Desde 480 vuelven a ir del tirado. */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-3">
                        <input type="date" value={diaSuelto} data-testid="dia-en-silencio"
                            onChange={(e) => setDiaSuelto(e.target.value)}
                            aria-label="Elige el día que va en silencio"
                            className="w-full sm:flex-1 min-w-0 bg-muted border border-border rounded-lg px-2.5 py-2 text-sm font-data focus:outline-none focus:border-brand" />
                        <button type="button" disabled={!diaSuelto}
                            data-testid="marcar-en-silencio"
                            onClick={() => { marcarEnSilencio(diaSuelto); setDiaSuelto(''); }}
                            className="w-full sm:w-auto rounded-lg bg-brand text-[#141414] font-bold text-sm px-3 py-2 disabled:opacity-40 flex-shrink-0">
                            Este día va en silencio
                        </button>
                    </div>
                    {diasCallados.length > 0 && (
                        <div className="space-y-2 mt-3" data-testid="lista-en-silencio">
                            {diasCallados.map(fecha => (
                                <div key={fecha}
                                    className="flex items-center justify-between gap-2 bg-muted rounded-lg px-3 py-2">
                                    {/* La fecha entera, aunque parta en dos líneas: «Sábado,
                                        19 de...» no le dice qué día se ha quedado callado. */}
                                    <span className="flex items-start gap-2 text-sm text-foreground min-w-0">
                                        <BellOff className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                                        <span>{enPalabras(fecha)}</span>
                                    </span>
                                    <button type="button" onClick={() => quitarSilencio(fecha)}
                                        aria-label={`Volver a avisarme el ${enPalabras(fecha)}`}
                                        className="text-xs font-bold text-muted-foreground hover:text-foreground flex-shrink-0 text-right">
                                        Vuelve a avisarme
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* «Cuando lo tengas, lo copias a los demás días: uno de entreno y uno de
                descanso, y ya está la semana. No se rellena siete veces.» */}
            {tieneAlgo && (
                <button type="button" onClick={copiar} disabled={guardando}
                    data-testid="copiar-al-otro-dia"
                    className="w-full surface surface-hover p-3 mt-3 flex items-center justify-center gap-2 text-sm font-bold text-foreground disabled:opacity-50">
                    <Copy className="w-4 h-4" />
                    {tipo === 'entreno' ? 'Copiar a mis días de descanso' : 'Copiar a mis días de entreno'}
                </button>
            )}

            <button type="button" onClick={guardar} disabled={guardando}
                data-testid="guardar-horario"
                className="w-full rounded-xl bg-brand text-[#141414] font-bold py-3 mt-4 hover:bg-brand/85 transition-colors disabled:opacity-50">
                {guardando ? 'Guardando...' : 'Guardar mi horario'}
            </button>
        </div>
    );
};

export default MiHorarioPage;
